import { Box, Grid, Typography } from "@mui/material";
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import EmploymentTesSalaryTableField from "../../../employment/tesSalaryTables/EmploymentTesSalaryTableField";
import InfoTooltip from "../../../../common/components/functional/InfoTooltip/InfoTooltip";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { TesSalaryTableSelectionProvenance } from "../../../tesSalaryTables/tesSalaryTablesApi";
import useTesSalaryComponents, {
  isExtraComponent,
} from "../../../../hooks/useTesSalaryComponents";
import { SalaryType } from "../../../orders/types/salaryType";
import LabelFieldRow from "./LabelFieldRow";
import SalaryComponentRow from "./SalaryComponentRow";
import TotalSalary from "./TotalSalary";
import { DEFAULT_SALARY_ZONE, getZoneLabel, SalaryZone } from "./common";
import usePrevious from "../../../../hooks/usePrevious";
import { FormKeys } from "../../../orders/editOrder/long/workAgreement/helpers";
import {
  applyTesSalaryTableSelectionToComponents,
  isTesManagedBaseComponentCode,
  isTesTableLockedComponentCode,
} from "./tesSalaryTableFormUtils";
import { format, isValid } from "date-fns";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import InputHelperText from "../../../../common/components/functional/error/InputHelperText";
import theme from "../../../../theme";
import { FlexVerticalAligned } from "../../../../common/components/styled/styled";

type Props = {
  validFrom?: Date | null;
  selectSalaryType?: boolean;
  xsCols?: [number, number];
  salaryType?: SalaryType;
  tesId: string;
  defaultValues?: { [key: string]: string };
  override?: boolean;
  edit?: boolean;
  resetToEmpty?: boolean;
  salaryZones?: (SalaryZone | string)[];
  hasTesSalaryTableSelection?: boolean;
  onManualBaseComponentChange?: () => void;
  salaryLabelActions?: ReactNode;
  tesSalaryTableProps?: {
    disabled?: boolean;
    showButton?: boolean;
    tesId?: string | null;
    workTitleId?: string | null;
    date?: Date | null;
    value?: TesSalaryTableSelectionProvenance | null;
    onAvailabilityChange?: (isAvailable: boolean) => void;
    onChange: (payload: {
      selection: TesSalaryTableSelectionProvenance;
      salaryAmount: string;
    }) => void;
  } | null;
};

const SalaryComponents = ({
  xsCols = [4, 8],
  tesId,
  validFrom,
  salaryType = SalaryType.Hourly,
  defaultValues,
  override,
  edit,
  resetToEmpty,
  salaryZones,
  hasTesSalaryTableSelection,
  onManualBaseComponentChange,
  salaryLabelActions,
  tesSalaryTableProps,
}: Props) => {
  const [hourlySalary, setHourlySalary] = useState("");
  const [zonedHourlySalaries, setZonedHourlySalaries] = useState<{
    [zone: string]: string;
  }>({});
  const [tesSalaryTablePickerAvailable, setTesSalaryTablePickerAvailable] =
    useState(false);
  const handleTesSalaryTableAvailabilityChange = useCallback(
    (isAvailable: boolean) => {
      setTesSalaryTablePickerAvailable(isAvailable);
      tesSalaryTableProps?.onAvailabilityChange?.(isAvailable);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tesSalaryTableProps?.onAvailabilityChange],
  );

  const { t } = useTranslation();
  const translations = useAppSelector(
    (state) => state.localized.salaryComponent,
  );

  const { control, setValue, trigger, setError, clearErrors, getValues } =
    useFormContext();
  const componentsValues = useWatch({ control, name: "components" });
  const zonedComponentsValues = useWatch({ control, name: "zonedComponents" });

  const { salaryComponents, salaryComponentsStatus } = useTesSalaryComponents({
    tesId,
    validFrom,
    salaryType,
    defaultComponentValues: defaultValues,
  });

  const isLoading = salaryComponentsStatus === "loading";
  const isCompleted = salaryComponentsStatus === "completed";
  const hasZones = salaryZones && salaryZones.length > 0;
  const hasBaseSalaryComponent =
    salaryComponents?.components.baseComponents.some(
      (component) =>
        component.code === "BASE" || component.code.includes("BASE_"),
    ) ?? false;
  const salaryComponentCodes = salaryComponents
    ? [
        ...salaryComponents.components.baseComponents,
        ...salaryComponents.components.personalComponents,
        ...salaryComponents.components.extraComponents,
      ].map((component) => component.code)
    : [];
  const tesSalaryPickerComponentCode =
    salaryComponents?.components.baseComponents.find((component) =>
      isTesManagedBaseComponentCode(component.code),
    )?.code ?? null;
  const isHourly = salaryType === SalaryType.Hourly;
  const salaryUnitLabel = isHourly
    ? t("common:hourlySalaryUnit")
    : t("common:monthlySalaryUnit");
  const salaryLabel = isHourly
    ? t("talentProfile:hourlyWork")
    : t("talentProfile:monthlySalaryTotal");
  const infoTooltipText = isHourly
    ? t("talentProfile:hourlyWorkTooltip")
    : t("talentProfile:monthlySalaryTooltip");
  const totalSalaryTooltipText = isHourly
    ? t("talentProfile:totalSalaryTooltip")
    : t("talentProfile:totalSalaryMonthlyTooltip");
  const validTesDate =
    tesSalaryTableProps?.date && isValid(tesSalaryTableProps.date)
      ? format(tesSalaryTableProps.date, isoFormat)
      : null;

  useEffect(() => {
    trigger(FormKeys.Components);
  }, [salaryComponents, trigger]);

  useEffect(() => {
    if (!hasBaseSalaryComponent) {
      setError(FormKeys.Components, {
        type: "manual",
        message: "baseSalaryComponentRequired",
      });
    } else {
      clearErrors(FormKeys.Components);
    }
  }, [hasBaseSalaryComponent, setError, clearErrors, t]);

  useEffect(() => {
    if (resetToEmpty) {
      Object.keys(componentsValues || {}).forEach((key) =>
        setValue(`components.${key}`, ""),
      );

      if (hasZones) {
        salaryZones.forEach((zone) => {
          Object.keys(componentsValues || {}).forEach((key) =>
            setValue(`zonedComponents.${zone}.${key}`, ""),
          );
        });
      }
    }
  }, [resetToEmpty, setValue, componentsValues, hasZones, salaryZones]);

  useEffect(() => {
    const currentComponents = getValues("components");
    const nextComponents = defaultValues || {};

    if (
      JSON.stringify(currentComponents || {}) !== JSON.stringify(nextComponents)
    ) {
      setValue("components", nextComponents);
    }

    // initialize zoned components if salaryZones exist
    if (hasZones && salaryZones) {
      salaryZones.forEach((zone) => {
        const currentZoneValues = getValues(`zonedComponents.${zone}`);
        if (!currentZoneValues) {
          setValue(`zonedComponents.${zone}`, nextComponents);
        }
      });
    }
  }, [defaultValues, getValues, setValue, hasZones, salaryZones]);

  const getSalaryTotals = useCallback(
    (type: "base" | "total", zone?: string) => {
      const componentValues = zone
        ? zonedComponentsValues?.[zone]
        : componentsValues;

      if (componentValues && salaryComponents) {
        const salaries =
          type === "base"
            ? salaryComponents?.components.baseComponents
            : [
                ...salaryComponents?.components.personalComponents,
                ...salaryComponents.components.baseComponents,
              ];

        const total = salaries.reduce((result, item) => {
          if (
            type === "base" &&
            item.isPartOfBaseSalary &&
            !isExtraComponent(item.code)
          ) {
            const value = parseFloat(componentValues[item.code] || "0") || 0;
            result = value + result;
          }
          if (type === "total" && !isExtraComponent(item.code)) {
            const value = parseFloat(componentValues[item.code] || "0") || 0;
            result = value + result;
          }
          return result;
        }, 0);

        return total ? total.toFixed(2) : "";
      }
      return "";
    },
    [componentsValues, zonedComponentsValues, salaryComponents],
  );

  useEffect(() => {
    if (isCompleted) {
      // set salary total default values after salary components are fetched
      const nextHourlySalary = getSalaryTotals("base");
      const nextTotalSalary = getSalaryTotals("total");

      setHourlySalary((current) =>
        current === nextHourlySalary ? current : nextHourlySalary,
      );
      if (getValues("totalSalary") !== nextTotalSalary) {
        setValue("totalSalary", nextTotalSalary);
      }

      if (hasZones) {
        const newZonedHourlySalaries: { [zone: string]: string } = {};
        salaryZones.forEach((zone) => {
          const nextZonedHourlySalary = getSalaryTotals("base", zone);
          const nextZonedTotalSalary = getSalaryTotals("total", zone);

          newZonedHourlySalaries[zone] = nextZonedHourlySalary;
          if (getValues(`zonedTotalSalary.${zone}`) !== nextZonedTotalSalary) {
            setValue(`zonedTotalSalary.${zone}`, nextZonedTotalSalary);
          }
        });
        setZonedHourlySalaries((current) =>
          JSON.stringify(current) === JSON.stringify(newZonedHourlySalaries)
            ? current
            : newZonedHourlySalaries,
        );
      }
    }
  }, [
    getSalaryTotals,
    getValues,
    setValue,
    isCompleted,
    hasZones,
    salaryZones,
  ]);

  useEffect(() => {
    if (!isCompleted || defaultValues || !salaryComponents) return;

    // TES can change while this form stays mounted.
    // Keep only keys from the currently loaded TES component schema so stale
    // keys from a previous TES cannot capture picker-applied values invisibly.
    const componentCodes = [
      ...salaryComponents.components.baseComponents,
      ...salaryComponents.components.personalComponents,
      ...salaryComponents.components.extraComponents,
    ].map((component) => component.code);

    const currentComponents = getValues("components") || {};
    const nextComponents = componentCodes.reduce(
      (result, code) => {
        result[code] =
          typeof currentComponents[code] === "string"
            ? currentComponents[code]
            : "";
        return result;
      },
      {} as { [key: string]: string },
    );

    if (
      JSON.stringify(currentComponents || {}) !== JSON.stringify(nextComponents)
    ) {
      setValue("components", nextComponents);
    }

    if (hasZones && salaryZones) {
      salaryZones.forEach((zone) => {
        const currentZoneComponents =
          getValues(`zonedComponents.${zone}`) || {};
        const nextZoneComponents = componentCodes.reduce(
          (result, code) => {
            result[code] =
              typeof currentZoneComponents[code] === "string"
                ? currentZoneComponents[code]
                : "";
            return result;
          },
          {} as { [key: string]: string },
        );

        if (
          JSON.stringify(currentZoneComponents || {}) !==
          JSON.stringify(nextZoneComponents)
        ) {
          setValue(`zonedComponents.${zone}`, nextZoneComponents);
        }
      });
    }
  }, [
    defaultValues,
    getValues,
    hasZones,
    isCompleted,
    salaryComponents,
    salaryZones,
    setValue,
  ]);

  // When salary type changes, replace components with new empty ones
  const previousSalaryType = usePrevious(salaryType);
  const resetSalaryTypeRef = useRef<SalaryType>();
  useEffect(() => {
    if (
      previousSalaryType &&
      previousSalaryType !== salaryType &&
      salaryComponents &&
      resetSalaryTypeRef.current !== salaryType
    ) {
      let result: { [key: string]: string } = {};
      const components = salaryComponents.components;
      for (const component of [
        ...components.baseComponents,
        ...components.personalComponents,
        ...components.extraComponents,
      ]) {
        result[component.code] = "";
      }
      setValue(FormKeys.Components, result);
      resetSalaryTypeRef.current = salaryType;
    }
  }, [previousSalaryType, salaryComponents, salaryType, setValue]);

  const onChange = (
    onChangeCb: (value: any) => void,
    value: any,
    isUserInput: boolean,
    zone?: string,
    componentCode?: string,
  ) => {
    // update salaryComponent values to react-hook-form state
    onChangeCb(value);

    if (
      isUserInput &&
      !zone &&
      componentCode &&
      isTesManagedBaseComponentCode(componentCode)
    ) {
      onManualBaseComponentChange?.();
    }

    if (zone) {
      // update zoned total salaries
      const newZonedHourlySalaries = { ...zonedHourlySalaries };
      newZonedHourlySalaries[zone] = getSalaryTotals("base", zone);
      setZonedHourlySalaries(newZonedHourlySalaries);
      setValue(`zonedTotalSalary.${zone}`, getSalaryTotals("total", zone));
    } else {
      // update regular total salaries (hourly: local state, total: react-hook-form state)
      setHourlySalary(getSalaryTotals("base"));
      setValue("totalSalary", getSalaryTotals("total"));
    }
  };

  const renderZoneHeaders = () => {
    if (!hasZones) return null;

    return (
      <>
        <Grid item xs={xsCols[0]}></Grid>
        <Grid item xs={3}>
          <Typography variant="h6" fontWeight="bold" textAlign="left">
            {DEFAULT_SALARY_ZONE}
          </Typography>
        </Grid>
        {salaryZones.map((zone) => (
          <Grid key={`header_${zone}`} item xs={3}>
            <Typography variant="h6" fontWeight="bold" textAlign="left">
              {getZoneLabel(zone as string)}
            </Typography>
          </Grid>
        ))}
        {/* spacer to align columns left (xs=4+3+3+2=12) */}
        <Grid item xs={2} />
      </>
    );
  };

  const renderComponentRowWithZones = (
    component: any,
    componentType: string,
  ) => {
    const isTesManagedLockedComponent = isTesTableLockedComponentCode({
      code: component.code,
      allComponentCodes: salaryComponentCodes,
      hasTesSalaryTableSelection: !!hasTesSalaryTableSelection,
      tesSalaryTablesAvailable: tesSalaryTablePickerAvailable,
    });
    const getComponentValue = (zone?: string) => {
      if (zone) {
        return zonedComponentsValues?.[zone]?.[component.code] || "";
      }
      return componentsValues?.[component.code] || "";
    };

    const addTesSalaryPicker =
      !!tesSalaryTableProps &&
      tesSalaryTableProps.showButton !== false &&
      !hasZones &&
      componentType === "baseComponents" &&
      component.code === tesSalaryPickerComponentCode;

    if (hasZones) {
      return (
        <Fragment key={`${componentType}_${component.code}`}>
          <Grid item textAlign="end" alignSelf="center" xs={xsCols[0]}>
            <Typography variant="secondary">{getLabel(component)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <SalaryComponentRow
              key={`muusuomi_${componentType}_${component.code}`}
              component={component}
              value={getComponentValue()}
              unit={salaryUnitLabel}
              xsCols={xsCols}
              onChange={(onChangeCb: any, value: any, isUserInput: boolean) =>
                onChange(
                  onChangeCb,
                  value,
                  isUserInput,
                  undefined,
                  component.code,
                )
              }
              disabled={(override && !edit) || isTesManagedLockedComponent}
              override={override}
              salaryType={salaryType}
            />
          </Grid>
          {addTesSalaryPicker && (
            <Grid item xs={1}>
              <EmploymentTesSalaryTableField
                showLabel={false}
                disabled={tesSalaryTableProps.disabled}
                tesId={tesSalaryTableProps.tesId}
                workTitleId={tesSalaryTableProps.workTitleId}
                date={validTesDate}
                salaryType={salaryType}
                hasZones={false}
                value={tesSalaryTableProps.value}
                onAvailabilityChange={handleTesSalaryTableAvailabilityChange}
                onSelectionUnavailable={() =>
                  tesSalaryTableProps.onAvailabilityChange?.(false)
                }
                onChange={({ selection, salaryAmount }) => {
                  const nextComponents =
                    applyTesSalaryTableSelectionToComponents({
                      components: getValues("components") || {},
                      salaryAmount,
                      baseComponentCode:
                        tesSalaryPickerComponentCode || undefined,
                    });

                  tesSalaryTableProps.onChange({
                    selection,
                    salaryAmount,
                  });
                  setValue("components", nextComponents, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  trigger(FormKeys.Components);
                }}
              />
            </Grid>
          )}
          <Grid item xs={3}>
            <SalaryComponentRow
              key={`pks_${componentType}_${component.code}`}
              component={component}
              value={getComponentValue(SalaryZone.PKS)}
              unit={salaryUnitLabel}
              xsCols={xsCols}
              onChange={(onChangeCb: any, value: any, isUserInput: boolean) =>
                onChange(onChangeCb, value, isUserInput, SalaryZone.PKS)
              }
              disabled={(override && !edit) || isTesManagedLockedComponent}
              override={override}
              zone={SalaryZone.PKS}
              salaryType={salaryType}
            />
          </Grid>
          {addTesSalaryPicker && (
            <Grid item xs={1}>
              <EmploymentTesSalaryTableField
                showLabel={false}
                disabled={tesSalaryTableProps.disabled}
                tesId={tesSalaryTableProps.tesId}
                workTitleId={tesSalaryTableProps.workTitleId}
                date={validTesDate}
                salaryType={salaryType}
                hasZones={false}
                value={tesSalaryTableProps.value}
                onAvailabilityChange={handleTesSalaryTableAvailabilityChange}
                onSelectionUnavailable={() =>
                  tesSalaryTableProps.onAvailabilityChange?.(false)
                }
                onChange={({ selection, salaryAmount }) => {
                  const nextComponents =
                    applyTesSalaryTableSelectionToComponents({
                      components: getValues("zonedComponents") || {},
                      salaryAmount,
                      baseComponentCode:
                        tesSalaryPickerComponentCode || undefined,
                    });

                  tesSalaryTableProps.onChange({
                    selection,
                    salaryAmount,
                  });
                  setValue("zonedComponents", nextComponents, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  trigger(FormKeys.Components);
                }}
              />
            </Grid>
          )}
          {!addTesSalaryPicker && <Grid item xs={2} />}
        </Fragment>
      );
    } else {
      return (
        <Fragment key={`${componentType}_${component.code}`}>
          <Grid item textAlign="end" alignSelf="center" xs={xsCols[0]}>
            <Typography variant="secondary">{getLabel(component)}</Typography>
          </Grid>

          <Grid item xs={12 - xsCols[0]}>
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              flexWrap="nowrap"
              minWidth={0}
            >
              <SalaryComponentRow
                key={`${componentType}_${component.code}`}
                component={component}
                value={getComponentValue()}
                unit={salaryUnitLabel}
                xsCols={xsCols}
                onChange={(onChangeCb: any, value: any, isUserInput: boolean) =>
                  onChange(
                    onChangeCb,
                    value,
                    isUserInput,
                    undefined,
                    component.code,
                  )
                }
                disabled={(override && !edit) || isTesManagedLockedComponent}
                override={override}
                salaryType={salaryType}
              />
              {addTesSalaryPicker && (
                <Box flex="1 1 auto" minWidth={0}>
                  <EmploymentTesSalaryTableField
                    showLabel={false}
                    disabled={tesSalaryTableProps.disabled}
                    tesId={tesSalaryTableProps.tesId}
                    workTitleId={tesSalaryTableProps.workTitleId}
                    date={validTesDate}
                    salaryType={salaryType}
                    hasZones={false}
                    value={tesSalaryTableProps.value}
                    onAvailabilityChange={
                      handleTesSalaryTableAvailabilityChange
                    }
                    onSelectionUnavailable={() =>
                      tesSalaryTableProps.onAvailabilityChange?.(false)
                    }
                    onChange={({ selection, salaryAmount }) => {
                      const nextComponents =
                        applyTesSalaryTableSelectionToComponents({
                          components: getValues("components") || {},
                          salaryAmount,
                          baseComponentCode:
                            tesSalaryPickerComponentCode || undefined,
                        });

                      tesSalaryTableProps.onChange({
                        selection,
                        salaryAmount,
                      });
                      setValue("components", nextComponents, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      trigger(FormKeys.Components);
                    }}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Fragment>
      );
    }
  };

  const getLabel = (component: any) => {
    if (!component.allowsZero) {
      return `${translations[component.code] || component.code}*`;
    }
    return translations[component.code] || component.code;
  };

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      {salaryComponents && hasBaseSalaryComponent ? (
        <>
          {renderZoneHeaders()}

          {salaryComponents.components.baseComponents.map((component) =>
            renderComponentRowWithZones(component, "baseComponents"),
          )}

          {hasZones ? (
            <>
              <Grid item textAlign="end" alignSelf="center" xs={xsCols[0]}>
                <Typography variant="secondary">
                  {t("talentProfile:hourlyWork")}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Box display="flex" alignItems="center" gap="22px">
                  <TotalSalary value={hourlySalary} unit={salaryUnitLabel} />
                  <InfoTooltip title={t("talentProfile:hourlyWorkTooltip")} />
                </Box>
              </Grid>
              {salaryZones.map((zone) => (
                <Grid key={`hourly_${zone}`} item xs={3}>
                  <Box display="flex" alignItems="center" gap="22px">
                    <TotalSalary
                      value={zonedHourlySalaries[zone as string] || ""}
                      unit={salaryUnitLabel}
                    />
                    <InfoTooltip title={t("talentProfile:hourlyWorkTooltip")} />
                  </Box>
                </Grid>
              ))}
              <Grid item xs={2} />
            </>
          ) : (
            <LabelFieldRow
              xsCols={xsCols}
              label={salaryLabel}
              labelActions={salaryLabelActions}
              fieldComponent={
                <FlexVerticalAligned gap={1.5}>
                  <TotalSalary value={hourlySalary} unit={salaryUnitLabel} />
                  <InfoTooltip title={infoTooltipText} />
                </FlexVerticalAligned>
              }
            />
          )}

          {!!salaryComponents.components.personalComponents.length && (
            <LabelFieldRow
              label=""
              xsCols={xsCols}
              fieldComponent={
                <FlexVerticalAligned>
                  <Typography
                    display="inline-block"
                    fontSize="12px"
                    lineHeight="12px"
                    variant="secondary"
                    width="147px"
                  >
                    {t("talentProfile:personalSalaryComponentsLabel")}
                  </Typography>
                  <InfoTooltip
                    title={t("talentProfile:personalSalaryComponentsTooltip")}
                  />
                </FlexVerticalAligned>
              }
            />
          )}

          {salaryComponents.components.personalComponents.map((component) =>
            renderComponentRowWithZones(component, "personalComponents"),
          )}

          {hasZones ? (
            <>
              <Grid item textAlign="end" alignSelf="center" xs={xsCols[0]}>
                <Typography variant="secondary">
                  {t("talentProfile:totalSalary")}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <FlexVerticalAligned gap="22px">
                  <Controller
                    name="totalSalary"
                    control={control}
                    render={({ field }) => (
                      <TotalSalary
                        value={field.value}
                        defaultValue={getSalaryTotals("total")}
                        unit={salaryUnitLabel}
                      />
                    )}
                  />
                  <InfoTooltip title={t("talentProfile:totalSalaryTooltip")} />
                </FlexVerticalAligned>
              </Grid>
              {salaryZones.map((zone) => (
                <Grid key={`total_${zone}`} item xs={3}>
                  <FlexVerticalAligned gap="22px">
                    <Controller
                      name={`zonedTotalSalary.${zone}`}
                      control={control}
                      render={({ field }) => (
                        <TotalSalary
                          value={field.value}
                          defaultValue={getSalaryTotals(
                            "total",
                            zone as string,
                          )}
                          unit={salaryUnitLabel}
                        />
                      )}
                    />
                    <InfoTooltip
                      title={t("talentProfile:totalSalaryTooltip")}
                    />
                  </FlexVerticalAligned>
                </Grid>
              ))}
              <Grid item xs={2} />
            </>
          ) : (
            <LabelFieldRow
              xsCols={xsCols}
              label={t("talentProfile:totalSalary")}
              fieldComponent={
                <Controller
                  name="totalSalary"
                  control={control}
                  render={({ field }) => (
                    <Box display="block">
                      <FlexVerticalAligned gap={1.5}>
                        <TotalSalary
                          value={field.value}
                          defaultValue={getSalaryTotals("total")}
                          unit={salaryUnitLabel}
                        />
                        <InfoTooltip title={totalSalaryTooltipText} />
                      </FlexVerticalAligned>
                      <InputHelperText
                        helperText={
                          field.value && field.value >= 10000
                            ? t("talentProfile:salaryOver10000")
                            : undefined
                        }
                        TypographyProps={{
                          style: {
                            marginTop: "-2px",
                            marginLeft: "5px",
                            color: theme.palette.warning.main,
                            width: "250px",
                          },
                        }}
                      />
                    </Box>
                  )}
                />
              }
            />
          )}

          {salaryComponents.components.extraComponents.map((component) =>
            renderComponentRowWithZones(component, "extraComponents"),
          )}
        </>
      ) : (
        isCompleted && (
          <>
            <Grid item xs={xsCols[0]} />
            <Grid item alignSelf="center" xs={xsCols[1]}>
              <Typography>
                {t("talentProfile:selectedSalaryTypeNotAvailable")}
              </Typography>
            </Grid>
          </>
        )
      )}
    </>
  );
};

export default SalaryComponents;
